import React from "react";
import { useTranslation } from "react-i18next";
import { Column, Row, ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import {
  Button,
  Avatar,
  AvatarImage,
  AvatarFallback,
  MenubarContent,
  MenubarItem,
  Menubar,
  MenubarTrigger,
  MenubarMenu,
} from "@/components/ui";
import { MoreVertical } from "lucide-react";
import { BulkMessage } from "@/__generated__/types";
import { useLocale } from "@/hooks/useLocale";
import { useNow } from "@/hooks/useNow";
import { formatDate } from "@/lib/formatDate";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import {
  getBulkMessageStatus,
  DATE_FORMAT,
  PLACEHOLDER_FALLBACK,
} from "./utils";
import { DeleteButton } from "@/pages/BulkMessaging/Components/DeleteButton";
import { StopButton } from "@/pages/BulkMessaging/Components/StopButton";

export const useBulkMessageColumns = (): ColumnDef<BulkMessage>[] => {
  const { t } = useTranslation();
  const locale = useLocale();
  const now = useNow();
  const { navToRoute } = useProtectedNavigation();

  const handleEditClick = (bulkMessage: BulkMessage) => {
    navToRoute("editBulkMessaging", { bulkMessageId: bulkMessage.id });
  };

  const columns: ColumnDef<BulkMessage>[] = [
    {
      id: "name",
      accessorFn: (row: BulkMessage) => row?.name || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.communityToolsOverview.columns.name")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => (
        <DataTable.TBodyCell>
          {row.original?.name || PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "status",
      accessorFn: (row: BulkMessage) => {
        if (!row) return "";
        const status = getBulkMessageStatus(row, now);
        return t(`bulkMessaging.communityToolsOverview.status.${status}`);
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.communityToolsOverview.columns.status")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => (
        <DataTable.TBodyCell>
          {row.original
            ? (() => {
                const status = getBulkMessageStatus(row.original, now);
                return t(
                  `bulkMessaging.communityToolsOverview.status.${status}`,
                );
              })()
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "createdAt",
      accessorFn: (row: BulkMessage) => {
        const { createdAt } = row || {};
        return createdAt ? formatDate(createdAt, DATE_FORMAT, { locale }) : "";
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.communityToolsOverview.columns.createdAt")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => (
        <DataTable.TBodyCell>
          {row.original?.createdAt
            ? formatDate(row.original.createdAt, DATE_FORMAT, { locale })
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "startSendingAt",
      accessorFn: (row: BulkMessage) => {
        const { startSendingAt } = row || {};
        return startSendingAt
          ? formatDate(startSendingAt, DATE_FORMAT, { locale })
          : "";
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.communityToolsOverview.columns.startDate")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => (
        <DataTable.TBodyCell>
          {row.original?.startSendingAt
            ? formatDate(row.original.startSendingAt, DATE_FORMAT, { locale })
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "createdBy",
      accessorFn: (row: BulkMessage) => {
        const { createdBy } = row || {};
        return createdBy
          ? `${createdBy.firstName} ${createdBy.lastName || ""}`.trim()
          : "";
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("bulkMessaging.communityToolsOverview.columns.createdBy"),
        avatarFilterLabel: true,
      },
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.communityToolsOverview.columns.createdBy")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => (
        <DataTable.TBodyCell>
          {row.original?.createdBy ? (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarImage
                  src={row.original.createdBy.profilePhoto?.url || undefined}
                />
                <AvatarFallback className="text-xs">
                  {row.original.createdBy.firstName?.[0]}
                  {row.original.createdBy.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <span>
                {`${row.original.createdBy.firstName} ${row.original.createdBy.lastName || ""}`.trim()}
              </span>
            </div>
          ) : (
            PLACEHOLDER_FALLBACK
          )}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "campaignType",
      accessorFn: (row: BulkMessage) => {
        if (!row?.campaignType) return "";
        return row.campaignType === "CAMPAIGN"
          ? t("bulkMessaging.filters.campaign")
          : t("bulkMessaging.filters.oneTimeSend");
      },
      enableSorting: true,
      enableColumnFilter: true,
      meta: {
        label: t("bulkMessaging.table.campaignType"),
      },
      header: ({ column }: { column: Column<BulkMessage> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("bulkMessaging.table.campaignType")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<BulkMessage> }) => {
        const { campaignType } = row.original || {};
        return (
          <DataTable.TBodyCell>
            {campaignType === "CAMPAIGN"
              ? t("bulkMessaging.filters.campaign")
              : campaignType === "ONE_TIME_SEND"
                ? t("bulkMessaging.filters.oneTimeSend")
                : PLACEHOLDER_FALLBACK}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<BulkMessage> }) => {
        const bulkMessage = row.original;
        if (!bulkMessage) {
          return (
            <DataTable.TBodyCell>{PLACEHOLDER_FALLBACK}</DataTable.TBodyCell>
          );
        }

        const status = getBulkMessageStatus(bulkMessage, now);

        return (
          <DataTable.TBodyCell isLastCell={true}>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditClick(bulkMessage)}
              >
                {t("bulkMessaging.communityToolsOverview.actions.edit")}
              </Button>

              <Menubar className="p-0">
                <MenubarMenu>
                  <MenubarTrigger
                    role="menuitem"
                    aria-label={t("dataTable.openTableActionsMenu")}
                    aria-haspopup="true"
                    className="cursor-pointer p-2 hover:bg-muted"
                  >
                    <MoreVertical className="h-4 w-4" aria-hidden="true" />
                  </MenubarTrigger>
                  <MenubarContent
                    align="end"
                    role="menu"
                    data-testid="table-actions-menu"
                    aria-label={t("dataTable.tableActions")}
                    aria-orientation="vertical"
                  >
                    {status === "scheduled" ? (
                      <MenubarItem
                        onClick={(e) => e.preventDefault()}
                        variant="destructive"
                        role="menuitem"
                        aria-label={t("bulkMessaging.table.delete.name")}
                      >
                        <DeleteButton id={bulkMessage.id}>
                          <Button
                            variant="ghost"
                            className="h-auto w-full justify-start p-0 font-normal hover:bg-transparent hover:text-destructive-foreground"
                          >
                            {t("bulkMessaging.table.delete.name")}
                          </Button>
                        </DeleteButton>
                      </MenubarItem>
                    ) : (
                      <MenubarItem
                        onClick={(e) => e.preventDefault()}
                        variant="destructive"
                        role="menuitem"
                        aria-label={t("bulkMessaging.table.stop.name")}
                      >
                        <StopButton id={bulkMessage.id}>
                          <Button
                            variant="ghost"
                            className="h-auto w-full justify-start p-0 font-normal hover:bg-transparent hover:text-destructive-foreground"
                          >
                            {t("bulkMessaging.table.stop.name")}
                          </Button>
                        </StopButton>
                      </MenubarItem>
                    )}
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return columns;
};
